/* Cart discount box (Cool Tea).
   Applying and removing a code really changes the cart (Shopify cart API), so the
   line prices, total, free shipping bar and checkout always agree. */
class CartDiscount extends HTMLElement {
  connectedCallback() {
    if (this.ready) return;
    this.ready = true;
    this.form = this.querySelector('.cart__discount-form');
    this.input = this.querySelector('input[name="discount"]');
    this.errorContainer = this.querySelector('.cart__discount-error');
    this.errorText = this.querySelector('.cart__discount-error-text');

    if (this.form) {
      this.form.addEventListener('submit', (event) => {
        event.preventDefault();
        const code = this.input ? this.input.value.trim() : '';
        if (code) this.applyDiscount(code);
      });
    }

    this.addEventListener('click', (event) => {
      const button = event.target.closest('.cart__discount-remove');
      if (!button) return;
      event.preventDefault();
      event.stopPropagation();
      const code = button.dataset.discountCode || button.getAttribute('data-discount-code');
      if (code) this.removeDiscount(code);
    });

    // Clean up the old "hidden discounts" list from the previous version of this file.
    try { localStorage.removeItem('removedDiscounts'); } catch (e) {}
  }

  root() {
    return (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  }

  strings(key, fallback) {
    return (window.cartStrings && window.cartStrings[key]) || fallback;
  }

  async getCart() {
    const response = await fetch(this.root() + 'cart.js', { headers: { Accept: 'application/json' } });
    return response.json();
  }

  codesOf(cart) {
    return (cart.discount_codes || []).map((d) => (typeof d === 'string' ? d : d.code)).filter(Boolean);
  }

  async setCodes(codes) {
    const response = await fetch(this.root() + 'cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ discount: codes.join(',') }),
    });
    if (!response.ok) throw new Error('Cart update failed');
    return response.json();
  }

  async applyDiscount(code) {
    this.hideError();
    if (this.input) this.input.disabled = true;
    try {
      const cart = await this.getCart();
      const existing = this.codesOf(cart);
      if (existing.some((c) => c.toUpperCase() === code.toUpperCase())) {
        this.showError(this.strings('discountAlreadyApplied', 'This discount code is already applied.'));
        return;
      }
      const updated = await this.setCodes(existing.concat(code));
      const entry = (updated.discount_codes || []).find((d) => (d.code || '').toUpperCase() === code.toUpperCase());
      if (!entry || entry.applicable === false) {
        await this.setCodes(existing);
        this.showError(this.strings('discountNotApplicable', 'This discount code is not valid or cannot be applied to your cart.'));
        return;
      }
      if (this.input) this.input.value = '';
      await this.refreshCart();
    } catch (error) {
      console.error('Error applying discount:', error);
      this.showError(this.strings('discountError', 'There was an error applying the discount code.'));
    } finally {
      if (this.input) this.input.disabled = false;
    }
  }

  async removeDiscount(code) {
    this.hideError();
    try {
      const cart = await this.getCart();
      const remaining = this.codesOf(cart).filter((c) => c.toUpperCase() !== code.toUpperCase());
      await this.setCodes(remaining);
      await this.refreshCart();
    } catch (error) {
      console.error('Error removing discount:', error);
      this.showError(this.strings('discountError', 'There was an error removing the discount code.'));
    }
  }

  async refreshCart() {
    const drawer = document.querySelector('cart-drawer');
    if (!drawer || !this.closest('cart-drawer')) {
      window.location.reload();
      return;
    }
    const response = await fetch(`${window.location.pathname}?section_id=cart-drawer`);
    const html = new DOMParser().parseFromString(await response.text(), 'text/html');

    const swap = (selector, useOuter) => {
      const fresh = html.querySelector(selector);
      const current = drawer.querySelector(selector);
      if (!fresh || !current) return;
      if (useOuter) current.outerHTML = fresh.outerHTML;
      else current.innerHTML = fresh.innerHTML;
    };
    swap('.free-shipping-bar', true);
    swap('cart-drawer-items', false);
    swap('.drawer__footer', false);

    const freshBubble = html.querySelector('.cart-count-bubble');
    const bubble = document.querySelector('.cart-count-bubble');
    if (freshBubble && bubble) bubble.textContent = freshBubble.textContent;

    if (typeof publish !== 'undefined' && typeof PUB_SUB_EVENTS !== 'undefined') {
      publish(PUB_SUB_EVENTS.cartUpdate, { source: 'cart-discount', cartData: await this.getCart() });
    }
  }

  showError(message) {
    if (!this.errorText || !this.errorContainer) return;
    this.errorText.textContent = message;
    this.errorContainer.classList.remove('hidden');
  }

  hideError() {
    if (!this.errorText || !this.errorContainer) return;
    this.errorContainer.classList.add('hidden');
    this.errorText.textContent = '';
  }
}

if (!customElements.get('cart-discount')) customElements.define('cart-discount', CartDiscount);